import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import {
  PyroLayer, GROUND_Y, SHELL_SHAPES, NOZZLE_TILT,
  V_BEAT, V_PHRASE, V_WALL, BEAT_PARTICLES, WALL_NOZZLES, WALL_PARTICLES
} from '../particle-layers/pyro-layer.js';
import { PosterBackdrop, BACKDROP_TIERS } from '../poster-backdrop.js';
import { cleanDisplayTitle, cleanDisplayArtist } from '../title-clean.js';

// Shared-rig contract: the light in this scene IS the pyrotechnics. There is no
// separate rig to share - every glow on the floor and in the smoke is computed
// from the gerbs that are burning right now.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pyro',
  family: 'stage',
  danceability: 0.90,
  energyLevel: 0.90,
  bpmMin: 115,
  bpmMax: 180,
  movement: 0.80,
  tonality: 0.60,
  prefers: { gridStrength: [0.60, 1.0], lowEnd: [0.45, 1.0] },
  enabled: true
};

// Pyro: stage pyrotechnics. Gerbs on every beat, comets on every bar, a wall of
// fire on the drops.
//
// THE ENGINE LIVES IN particle-layers/pyro-layer.js - the physics, the
// crackle, the blackbody shading, the trails, the smoke, the flame columns,
// the shells and the wall are all there, extracted verbatim so MainStage can
// fire the same charges. What stays here is what makes this piece PYRO: the
// ring of nozzles all the way round the frame, the beat/bar/phrase schedule
// and the drop. The layer's own header carries the tuning history of the
// charges; this one carries the cadence's.
//
// THE BACKGROUND IS POSTER'S (poster-backdrop.js, since 2026-09-26): the dot
// grid, the LED wall with its wave on the bar and its full light on the drop,
// the two radial glows either side of the cover and the title in dead neon
// tube - everything of Poster but its pictograms. It replaced a bare deck
// line: the same cadence drives it (beat, bar, phrase), the wall of fire
// lights the LED wall for a bar, and the charges still throw their own warm
// glow onto it through the layer's glow slots.
//
// FEWER, AND MUCH BIGGER. This started out relentless - a gerb on every beat,
// with bar, phrase and drop stacking on top - and relentless turned out to be
// the wrong instrument. Counted on screen, a phrase downbeat fired seven
// charges in the same frame and about fourteen were alive at once; at that
// density no single one of them is ever read, and the frame settles into a
// texture of sparks rather than a series of events. A firework is an EVENT.
//
// So the cadence is roughly a third of what it was and every charge is spent
// on scale instead: a beat gerb now reaches most of the way up the frame where
// it used to reach four tenths, and carries three times the grains. The budget
// is the same - it is concentrated rather than increased.
//
// THE COLOUR IS A TEMPERATURE, NOT A PALETTE. A pyro spark is burning titanium:
// white-blue at the nozzle, yellow through the middle of its flight, orange as
// it slows, dark red before it goes out. That progression is the whole reason
// the image reads as pyrotechnics rather than as a particle effect, so the
// sparks are shaded through Ember's blackbody curve, imported unchanged. The
// palette gets the smoke, the ambient glow, the floor and the COMETS - which is
// exactly what makes a comet read as a different object from a gerb.
//
// WORLD SPACE IS THE SAFE ZONE'S SPACE: x in [-aspect, aspect], y in [-1, 1],
// written straight to clip space. The nozzles are then placed in the same
// coordinates the cover's rectangle is given in, so keeping them out from under
// it is arithmetic rather than unprojection.

// One, not three. Three phrase gerbs at the new size fill the frame with a
// single wall of light and the shell that fires with them is invisible inside
// it.
const PHRASE_GERBS = 1;
// CLOSER (1/10/2026, « grossis les effets, plus impressionnants, caméra plus
// proche »). The whole show is drawn as if the camera had stepped in: every
// trajectory, flame, puff and grain grows by this factor about the middle of
// the floor, while the nozzles stay exactly where they were on screen (they
// are placed at the layer's fromScreen()). Gerbs now leave the top of the
// frame and the grains are fat sparks, not dust. ?pyrozoom=1 is the old view.
const ZOOM = 1.7;
// ...and every charge carries more grains: closer, a gerb is a denser fountain.
const GRAIN_GAIN = 1.3;
const ANTICIPATION_BEATS = 4;

const TIER = {
  0: { max: 2500, trails: false, smoke: false, reflection: false, crackle: true, flames: false },
  1: { max: 5000, trails: true, smoke: true, reflection: false, crackle: true, flames: true },
  2: { max: 8000, trails: true, smoke: true, reflection: true, crackle: true, flames: true }
};
const DEFAULT_TIER = 2;
// The backdrop's LED wall lights up for this many beats after a wall of fire
// - Poster's own drop measure - but at a fraction of Poster's full white:
// behind three thousand grains of fire a wall at full power took the frame
// to a mean of 240 on 255. At 0.25 it reads as the wall waking up under the
// fire, and stays under the bloom threshold on its own.
const BACKDROP_GAIN_FOND = 4.0;
const BACKDROP_GAIN_TYPO = 7.0;
const BACKDROP_WALL_BEATS = 4;
const BACKDROP_WALL_LEVEL = 0.25;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================

export class PyroVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];
    this._max = this._layout.max;

    this._time = 0;
    this._paused = false;
    this._aspect = 1.78;

    // --- debug
    this._trailsOn = this._layout.trails && urlParam('pyrotrail') !== '0';
    this._crackleOn = urlParam('pyrocrackle') !== '0';
    this._forceBurst = urlParam('pyroburst') === '1';
    this._forceComet = urlParam('pyrocomet') === '1';
    this._forceWall = urlParam('pyrowall') === '1';
    const sh = urlParam('pyroshell');
    this._forceShell = sh != null
      ? (SHELL_SHAPES.indexOf(sh) >= 0 ? SHELL_SHAPES.indexOf(sh) : 0) : null;
    this._forceFlame = urlParam('pyroflame') === '1';
    this._phraseCount = 0;
    this._barCount = 0;
    this._beatParity = 0;
    this._phraseCount = 0;
    this._shellTurn = 0;
    this._lastFlameAt = -1e9;

    // --- beat / bar / phrase edges
    this._lastBeatPhase = 0;
    this._lastBarPhase = 0;
    this._lastPhraseIndex = null;
    this._beatCount = 0;
    this._nozzleTurn = 0;
    this._lastLow = 0;
    this._noGridLastFire = -1e9;

    // --- drop
    this._anticipation = 0;
    this._lastDropAt = -1e9;

    this._beatSeconds = 0.5;
  }

  init(scene, camera) {
    this._scene = scene;

    // ---- the charges: sparks, reflection, trails, smoke, flames, in the
    // layer, drawn straight to clip space exactly as they always were.
    this._layer = new PyroLayer({
      max: this._max,
      trails: this._trailsOn,
      smoke: this._layout.smoke,
      reflection: this._layout.reflection,
      crackle: this._crackleOn,
      flames: this._layout.flames,
      linewidth: this._tier === 2 ? 1.7 : 1.3,
      gain: Number.isFinite(parseFloat(urlParam('pyrogain'))) ? parseFloat(urlParam('pyrogain')) : GRAIN_GAIN,   // ?pyrogain=1: the old density
      space: 'clip'
    });
    this._layer.init(scene);
    const zq = parseFloat(urlParam('pyrozoom'));
    this._layer.setZoom(Number.isFinite(zq) && zq > 0 ? zq : ZOOM, 0, GROUND_Y);
    // The background takes the charges' glows in SCREEN coordinates: the
    // layer's are simulation coordinates, which the zoom no longer equals.
    this._glowSim = this._layer.glow;
    this._glow = this._glowSim.map(() => new THREE.Vector3());

    // ---- background: Poster's, lit by the charges' glow slots. Rendered
    // first, so the layer's point size follows the real buffer before any
    // spark is drawn.
    const tierOpts = BACKDROP_TIERS[this._tier] || BACKDROP_TIERS[0];
    // Its hue draws come from a generator of its own, not Math.random: the
    // charges' random stream is Pyro's, and the equivalence test that keeps
    // this file honest replays it seeded.
    this._graine = 1;
    this._backdrop = new PosterBackdrop({
      points: tierOpts.points, vague: tierOpts.vague, mur: tierOpts.mur,
      // LE DÉCOR PLUS PRÉSENT QUE CHEZ POSTER, et c'est propre à pyro. Chez
      // poster le fond doit s'effacer derrière deux pictogrammes ; ici il est
      // seul entre deux salves de feu, et il disparaissait. Mesuré sur la
      // grille de points, hors des charges : 8,9 sur 255 au départ, 18,8 à
      // gainFond 3,5 et 24,8 à 5 - la grille ne se lisait que du côté gauche
      // du cadre. gainTypo relève le bandeau en néon ÉTEINT, qui n'était qu'un
      // liseré : le caractère qui s'amorce sur le temps garde sa clarté
      // propre, donc le contraste entre le tube mort et l'amorce - le sujet du
      // bandeau - est préservé.
      gainFond: BACKDROP_GAIN_FOND, gainTypo: BACKDROP_GAIN_TYPO,
      glows: this._glow, renderOrder: -3000,
      random: () => { this._graine = (this._graine * 1103515245 + 12345) % 2147483648; return this._graine / 2147483648; }
    });
    this._backdrop.init(scene);
    const self = this;
    this._backdrop.mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._layer.setResolution(_size.x, _size.y);
    };
    this._backdrop.setPalette(null);
    this._murUntil = -1;
    this._pisteVue = undefined;

    // The wall walks its own evenly spaced line across the floor (see
    // _fireWall); the objects are built once and only their x moves.
    this._wallLine = [];
    for (let i = 0; i < WALL_NOZZLES; i++) {
      this._wallLine.push({ x: 0, y: GROUND_Y, tilt: 0, dx: 0, dy: 1, edge: 'floor' });
    }

    this._nozzles = [];
    this._updateNozzles(1.78, 0.5);
  }

  // Nozzles all the way round the frame, not just across the floor. Each one
  // carries its own position AND the direction it fires in, which is the
  // inward normal of the edge it sits on - so `tilt` stays what it always was,
  // an angle where 0 is "along my own barrel", and every existing cone,
  // jitter and wall calculation keeps working unchanged.
  //
  // The angle convention comes from the layer's callers: a velocity is built
  // as (sin(a), cos(a)), so a = 0 fires up, +PI/2 right, -PI/2 left, PI down.
  //
  // NONE of the floor nozzles sits under the cover - the cover is given in
  // this same space, so its half-width is subtracted directly and the inner
  // pair is pushed out past it. A nozzle firing straight up through the
  // artwork would put the safe zone's attenuation right where the image is
  // brightest. The side and top nozzles fire across rather than through it,
  // so they only avoid its vertical band.
  _updateNozzles(aspect, progress) {
    // R19: the nozzles keep clear of the stage block, not of the moved cover.
    const half = safeZoneUniforms.uStageHalfSize.value;
    const c = safeZoneUniforms.uStageCenter.value;
    const clearX = Math.abs(c.x) + half.x + 0.12;
    const clearY = Math.abs(c.y) + half.y + 0.12;
    const n = [];

    // --- the floor: six, as before, and still the backbone of the show ---
    const outer = aspect * 0.90;
    const inner = Math.max(clearX, aspect * 0.22);
    const xs = [];
    for (let i = 0; i < 3; i++) {
      const d = inner + (outer - inner) * (i / 2);
      xs[i] = -d; xs[5 - i] = d;
    }
    for (let i = 0; i < 6; i++) n.push({ x: xs[i], y: GROUND_Y, tilt: 0, dx: 0, dy: 1, edge: 'floor' });

    // --- the two sides: firing inward, clear of the cover's own band ---
    const sy = [clearY + 0.18, -clearY - 0.10];
    for (let k = 0; k < sy.length; k++) {
      n.push({ x: -aspect * 0.94, y: sy[k], tilt: Math.PI / 2, dx: 1, dy: 0, edge: 'left' });
      n.push({ x: aspect * 0.94, y: sy[k], tilt: -Math.PI / 2, dx: -1, dy: 0, edge: 'right' });
    }

    // --- the ceiling: firing down, outside the cover's width ---
    for (let k = 0; k < 2; k++) {
      const d = clearX + 0.25 + k * (aspect * 0.32);
      n.push({ x: -Math.min(d, aspect * 0.9), y: 0.94, tilt: Math.PI, dx: 0, dy: -1, edge: 'ceiling' });
      n.push({ x: Math.min(d, aspect * 0.9), y: 0.94, tilt: Math.PI, dx: 0, dy: -1, edge: 'ceiling' });
    }

    // Screen positions above; the layer simulates in its own coordinates.
    for (const z of n) { const q = this._layer.fromScreen(z.x, z.y); z.x = q[0]; z.y = q[1]; }
    this._nozzles = n;
    // progress opens the stage: the floor alone at the ends of a track, the
    // whole perimeter through the middle. Same gesture as before - four then
    // six - just extended to the edges that now exist.
    const arc = Math.sin(clamp01(progress) * Math.PI);
    this._activeNozzles = arc > 0.45 ? n.length : 4;
  }

  // A nozzle by index, wrapped into whatever is currently active. Every firing
  // site goes through here so the progress gate applies once, in one place.
  _nozzle(i) {
    const list = this._nozzles;
    const count = Math.max(1, Math.min(this._activeNozzles || list.length, list.length));
    return list[((i % count) + count) % count];
  }

  _fireWall() {
    // The wall is the one gesture that does NOT use the nozzle ring: it is a
    // curtain drawn right across the floor, so it walks its own evenly spaced
    // line and fires each position straight up.
    const a = this._aspect;
    for (let i = 0; i < WALL_NOZZLES; i++) {
      this._wallLine[i].x = this._layer.fromScreen(-a * 0.95 + (2 * a * 0.95) * (i / (WALL_NOZZLES - 1)), GROUND_Y)[0];
    }
    this._layer.fireWall(this._wallLine, V_WALL[0], V_WALL[1], Math.round(WALL_PARTICLES / WALL_NOZZLES));
    // Poster's drop: the LED wall fully lit for a measure, then off at once.
    this._murUntil = this._time + BACKDROP_WALL_BEATS * this._beatSeconds;
  }

  // --- the frame ----------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    const low = clamp01(bands.low), high = clamp01(bands.high);
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 128;
    this._beatSeconds = 60 / Math.max(60, bpm);
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    this._aspect = aspect;
    this._updateNozzles(aspect, profile.progress || 0);

    // --- palette. The comets, the smoke and the ambience only.
    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPalette) {
      this._lastPalette = palette;
      const vivid = palette.vividColors && palette.vividColors.length
        ? palette.vividColors : [0x7fd4ff];
      const c = this._layer.cometColor.set(vivid[0]);
      // A comet is a coloured star. Anything washed out reads as a white gerb
      // that got dirty, so the saturation has a floor.
      const hsl = _hsl; c.getHSL(hsl);
      c.setHSL(hsl.h, Math.max(0.80, hsl.s), Math.min(0.68, Math.max(0.52, hsl.l)));
      c.getHSL(_hsl0);
      // A different entry for the shells, pushed away in hue if the palette
      // only offered one: two coloured layers that share a colour are one
      // layer that happens to be in two places.
      const sc = this._layer.shellColor.set(vivid.length > 1 ? vivid[1] : vivid[0]);
      sc.getHSL(hsl);
      let h2 = hsl.h;
      const dh = Math.abs(h2 - _hsl0.h);
      if (Math.min(dh, 1 - dh) < 0.12) h2 = (h2 + 0.42) % 1;
      sc.setHSL(h2, Math.max(0.78, hsl.s), Math.min(0.70, Math.max(0.54, hsl.l)));
      const dom = palette.dominantColors && palette.dominantColors.length
        ? palette.dominantColors[0] : 0x2b3d63;
      const smokeCol = this._layer.smokeColor;
      if (smokeCol) smokeCol.set(dom).lerp(_white, 0.35);
      this._backdrop.setPalette(palette);
    }

    // --- the title, in the backdrop's dead neon tube, as Poster shows it.
    const piste = (profile.title || '') + '\u0000' + (profile.artist || '') + '\u0000' + (profile.trackId || '');
    if (piste !== this._pisteVue) {
      this._pisteVue = piste;
      this._backdrop.setTitle(cleanDisplayTitle(profile.title) || cleanDisplayArtist(profile.artist)
        || profile.album || null);
    }

    // --- the grid: beat, bar, phrase
    const hasGrid = director.hasReliableGrid !== false && profile.beatPhase != null;
    const beatPhase = profile.beatPhase || 0;
    const barPhase = profile.barPhase || 0;

    // Anticipation: four beats before a drop the gerbs grow. Computed before
    // anything fires, because it scales what fires.
    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null) {
      const beatsOut = (ev.msUntil / 1000) / this._beatSeconds;
      this._anticipation = clamp01(1 - beatsOut / ANTICIPATION_BEATS);
    } else {
      this._anticipation *= Math.exp(-dt * 3.0);
    }

    // bands.low moves the gerbs by a quarter either way, and the anticipation
    // rides on top of that.
    const power = (0.75 + 0.5 * low) * (1 + this._anticipation * 0.9);

    if (hasGrid) {
      if (beatPhase < this._lastBeatPhase) this._onBeat(power, high);
      if (barPhase < this._lastBarPhase) this._onBar();
      this._lastBeatPhase = beatPhase;
      this._lastBarPhase = barPhase;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhraseIndex != null && phrase !== this._lastPhraseIndex) {
        this._onPhrase(power);
      }
      this._lastPhraseIndex = phrase;
    } else {
      // WITHOUT A GRID the piece stays just as dense - only the alignment is
      // lost. Rises in the low band fire gerbs, strong peaks fire comets, and a
      // floor keeps something happening even through a flat passage.
      const rise = low - this._lastLow;
      const since = this._time - this._noGridLastFire;
      if ((rise > 0.10 && since > 0.22) || since > 0.62) {
        this._onBeat(power, high);
        this._noGridLastFire = this._time;
        if (low > 0.78 && Math.random() < 0.35) this._onBar();
      }
      this._lastLow = low;
    }

    // --- drops
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0
        && this._time - this._lastDropAt > 2.0) {
      this._lastDropAt = this._time;
      this._fireWall();
      for (let i = 0; i < 2; i++) this._layer.fireComet(this._nozzle((this._nozzleTurn + i * 3)));
      // The drop takes everything: the wall, a salvo of comets, two shells of
      // different shapes, and flame columns on the outer nozzles.
      // One shell on the drop, not two. The wall is already the largest thing
      // the piece does; a second shell inside it is not seen.
      this._layer.fireShell(this._nozzle(1), (this._shellTurn + 1) % SHELL_SHAPES.length);
      for (let i = 0; i < 4; i++) this._layer.fireFlame(this._nozzle(i < 2 ? i : i + 2));
    }

    // --- debug triggers, one shot each
    if (this._forceBurst) { this._forceBurst = false; this._onBeat(power, high); }
    if (this._forceComet) { this._forceComet = false; this._onBar(); }
    if (this._forceWall) { this._forceWall = false; this._fireWall(); }
    if (this._forceShell != null) { this._layer.fireShell(this._nozzle(2), this._forceShell); this._forceShell = null; }
    if (this._forceFlame) { this._forceFlame = false; for (let i = 0; i < 3; i++) this._layer.fireFlame(this._nozzle(i * 2)); }
    // A flame column on a low-end hit, so the layer appears between phrases
    // too. 0.62 and not the 0.88 this started at: measured against a realistic
    // band envelope, 0.88 was crossed six times in three minutes - one column
    // every thirty seconds, which is invisible next to a gerb every four
    // tenths of a second.
    if (low > 0.62 && this._time - this._lastFlameAt > 2.4) {
      this._lastFlameAt = this._time;
      this._layer.fireFlame(this._nozzle((this._nozzleTurn * 5 + 2)));
    }

    this._layer.update(dt, high, profile.progress || 0);
    for (let i = 0; i < this._glow.length; i++) {
      const g = this._glowSim[i], q = this._layer.toScreen(g.x, g.y);
      this._glow[i].set(q[0], q[1], g.z);
    }
    this._backdrop.update(dt, Math.max(0.12, this._beatSeconds), this._time < this._murUntil ? BACKDROP_WALL_LEVEL : 0);
    if (this._layer.flash && profile.director) profile.director.flash = 1;
  }

  // A GERB EVERY OTHER BEAT. At the new size one charge takes about two and a
  // half seconds to rise and fall, so firing every beat kept six of them alive
  // at once and they merged into a continuous curtain - the thing that made
  // the old tuning read as texture rather than as fireworks. Every second beat
  // leaves each plume long enough alone to be seen as one object.
  //
  // The skipped beat is not silent: the crackle of the charge before it is
  // still going off, which is what carries the pulse between launches.
  _onBeat(power, high) {
    // Every beat reaches the backdrop (its pulse and the neon character),
    // including the ones the gerbs sit out.
    this._backdrop.onBeat();
    this._beatParity = (this._beatParity + 1) & 1;
    if (this._beatParity === 1) return;
    const n = this._activeNozzles;
    // A rotating pattern rather than a cycle: a plain left-to-right sweep reads
    // as a machine, and a random choice reads as noise. Stepping by two through
    // six positions visits them all in a different order each time round.
    const idx = (this._nozzleTurn * 2 + ((this._nozzleTurn / 3) | 0)) % n;
    this._nozzleTurn = (this._nozzleTurn + 1) % 12;
    const slot = n === 6 ? idx : (idx < 2 ? idx : idx + 2);
    const noz = this._nozzle(slot);
    const count = Math.round(BEAT_PARTICLES * (0.7 + 0.5 * power) * this._layer.scale());
    // The cone leans AWAY from the middle of the frame, whichever edge the
    // nozzle is on - so a floor nozzle on the left still leans left, and one
    // on the ceiling leans the same way relative to its own barrel.
    this._layer.fireGerb(noz, V_BEAT[0] * power, V_BEAT[1] * power, count,
                         noz.x < 0 ? -NOZZLE_TILT : NOZZLE_TILT, 0);
    this._layer.emitSmoke(noz.x);
  }

  // A COMET ON MOST BARS, A SHELL ON EVERY FOURTH. Tied to the phrase alone,
  // shells fired once every thirty-six seconds against a gerb every four
  // tenths - counted over three simulated minutes - which is not a layer, it
  // is an accident that happens twice a track. On the bar they land about
  // every seven seconds and become part of the show.
  // A SHELL ON EVERY FOURTH BAR, A COMET ON EVERY SECOND, AND NOTHING ON THE
  // OTHER TWO. It used to be a comet on three bars in four, which at the new
  // burst size means a comet apogee permanently somewhere in the frame. The
  // empty bars are what let the gerbs be the subject.
  _onBar() {
    this._backdrop.onBar();
    this._barCount = (this._barCount + 1) % 4;
    const noz = this._nozzle((this._nozzleTurn * 5));
    if (this._barCount === 0) {
      this._shellTurn = (this._shellTurn + 1) % SHELL_SHAPES.length;
      this._layer.fireShell(noz, this._shellTurn);
    } else if (this._barCount === 2) {
      this._layer.fireComet(noz);
    }
  }

  _onPhrase(power) {
    this._backdrop.onPhrase();
    // A shell on every phrase, of the shape the bar cycle has not just used -
    // and the flame column on the OTHER phrases rather than with it. Fired
    // together they occupy the same part of the frame at the same moment and
    // the column, which is the slower and quieter of the two, is simply not
    // seen.
    this._phraseCount = (this._phraseCount + 1) & 1;
    this._shellTurn = (this._shellTurn + 2) % SHELL_SHAPES.length;
    if (this._phraseCount === 0) {
      this._layer.fireShell(this._nozzle((this._nozzleTurn * 3 + 1)), this._shellTurn);
    } else {
      this._layer.fireFlame(this._nozzle((this._nozzleTurn * 5 + 4)));
    }
    for (let i = 0; i < PHRASE_GERBS; i++) {
      const noz = this._nozzle((this._nozzleTurn + i * 2));
      this._layer.fireGerb(noz, V_PHRASE[0] * power, V_PHRASE[1] * power,
                           Math.round(BEAT_PARTICLES * 1.15 * this._layer.scale()),
                           noz.x < 0 ? -NOZZLE_TILT : NOZZLE_TILT, 0);
      this._layer.emitSmoke(noz.x);
    }
  }

  dispose() {
    if (this._backdrop) {
      this._backdrop.mesh.onBeforeRender = null;
      this._backdrop.dispose();
      this._backdrop = null;
    }
    if (this._layer) {
      this._layer.dispose();
      this._layer = null;
    }
  }
}

// Module-scope scratch, so update() allocates nothing.
const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };
const _hsl0 = { h: 0, s: 0, l: 0 };
const _white = new THREE.Color(0xffffff);
